import { Button } from './ui/button';
import {
  ArrowLeft,
  Clock,
  CalendarDays,
  Repeat,
  Music,
} from 'lucide-react';
import { Program, Track } from '../data/mockData';
import { API_BASE_URL } from '../../store/api';
import { format } from 'date-fns';
import { parseApiDate } from '../utils/dateTime';
import {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from './ui/breadcrumb';

interface ProgramDetailPageProps {
  program: Program;
  tracks: Track[];
  channelId: string | number;
  onBack: () => void;
}

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export function ProgramDetailPage({ program, tracks, channelId, onBack }: ProgramDetailPageProps) {
  const start = parseApiDate(program.startDateTime);
  const end = parseApiDate(program.endDateTime);
  const track = program.trackId ? tracks.find((t) => String(t.id) === String(program.trackId)) : null;

  const thumbnailUrl = program.thumbnail
    ? program.thumbnail.startsWith('/')
      ? `${API_BASE_URL}${program.thumbnail}`
      : program.thumbnail
    : null;

  return (
    <div className="p-6 sm:p-8 max-w-3xl mx-auto">
      <Breadcrumb className="mb-4">
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbLink
              className="text-gray-400 hover:text-white cursor-pointer"
              onClick={onBack}
            >
              Events
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator className="text-gray-500" />
          <BreadcrumbItem>
            <BreadcrumbPage className="text-white">{program.title}</BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>

      {/* Cover image with title overlay */}
      {thumbnailUrl ? (
        <div className="relative rounded-xl overflow-hidden mb-6">
          <img
            src={thumbnailUrl}
            alt={program.title}
            loading="lazy"
            className="w-full h-64 sm:h-80 object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 p-6">
            <h1 className="text-2xl sm:text-3xl font-bold text-white drop-shadow-lg">
              {program.title}
            </h1>
          </div>
        </div>
      ) : (
        <h1 className="text-2xl sm:text-3xl font-bold text-white mb-6">
          {program.title}
        </h1>
      )}

      {/* Date & Time */}
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <span className="inline-flex items-center gap-2 text-sm text-gray-200 bg-gray-800 border border-gray-700 rounded-lg px-3 py-2">
          <CalendarDays className="size-4 text-blue-400" />
          {format(start, 'EEEE, MMM dd, yyyy')}
        </span>
        <span className="inline-flex items-center gap-2 text-sm text-gray-200 bg-gray-800 border border-gray-700 rounded-lg px-3 py-2">
          <Clock className="size-4 text-blue-400" />
          {format(start, 'HH:mm')} – {format(end, 'HH:mm')}
        </span>
      </div>

      {/* Description */}
      {program.description && (
        <div className="mb-6">
          <h2 className="text-lg font-semibold text-white mb-2">Description</h2>
          <p className="text-gray-300 leading-relaxed whitespace-pre-line">
            {program.description}
          </p>
        </div>
      )}

      {/* Schedule Info */}
      <div className="bg-gray-800 border border-gray-700 rounded-xl p-5 mb-6">
        <h2 className="text-lg font-semibold text-white mb-3">Schedule</h2>
        <div className="space-y-2 text-sm text-gray-300">
          <div className="flex items-center gap-2">
            <span className="text-gray-400 w-28 shrink-0">Type:</span>
            <span className="capitalize">{program.scheduleType}</span>
          </div>
          {program.scheduleType === 'recurring' && program.recurrence && (
            <>
              <div className="flex items-center gap-2">
                <span className="text-gray-400 w-28 shrink-0">Frequency:</span>
                <span className="inline-flex items-center gap-1.5">
                  <Repeat className="size-3.5 text-purple-400" />
                  <span className="capitalize">{program.recurrence.frequency}</span>
                </span>
              </div>
              {program.recurrence.frequency === 'weekly' &&
                program.recurrence.daysOfWeek &&
                program.recurrence.daysOfWeek.length > 0 && (
                  <div className="flex items-center gap-2">
                    <span className="text-gray-400 w-28 shrink-0">Repeat on:</span>
                    <div className="flex gap-1.5 flex-wrap">
                      {program.recurrence.daysOfWeek
                        .slice()
                        .sort((a, b) => a - b)
                        .map((d) => (
                          <span
                            key={d}
                            className="text-xs bg-purple-500/15 text-purple-300 border border-purple-500/25 rounded-md px-2 py-0.5"
                          >
                            {DAY_NAMES[d]}
                          </span>
                        ))}
                    </div>
                  </div>
                )}
              {program.recurrence.endDate && (
                <div className="flex items-center gap-2">
                  <span className="text-gray-400 w-28 shrink-0">Until:</span>
                  <span>{format(parseApiDate(program.recurrence.endDate), 'MMM dd, yyyy')}</span>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Audio Preview */}
      {track ? (
        <div className="bg-gray-800 border border-gray-700 rounded-xl p-5">
          <h2 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
            <Music className="size-5 text-blue-400" />
            Audio Preview
          </h2>
          <p className="text-sm text-gray-400 mb-3">{track.fileName}</p>
          <audio
            controls
            className="w-full"
            src={`${API_BASE_URL}/tracks/${track.id}/download`}
          >
            Your browser does not support the audio element.
          </audio>
        </div>
      ) : (
        <div className="bg-yellow-900/30 border border-yellow-700/50 rounded-xl p-5 flex items-center gap-3">
          <Music className="size-5 text-yellow-400 shrink-0" />
          <p className="text-sm text-yellow-200">
            No audio track has been assigned to this program yet.
          </p>
        </div>
      )}
    </div>
  );
}
